import { IContentControlManager } from 'app/js/src/domain/content-control/manager';
import { IContentControlVideoSource } from 'app/js/src/domain/content-control/i-content-control-video-source';
import { ContentControlledEditingDirective } from 'app/js/src/domain/content-control/editing-directive';
import {
  ContentControlledEventActionEnum,
  ContentControlledMediaTypeEnum,
} from 'app/js/src/domain/content-control/enums';
import { ContentControlCanvasService } from 'app/js/src/infrastructure/content-control/content-control-canvas.service';
import {
  CCBlurRect,
  CCMosaicRect,
  CCRect,
  ICanvasBaseObject,
} from 'app/js/src/domain/content-control/canvas';
import { IContentControlStudioTimeline } from '../content-control-studio-timeline/i-content-control-studio-timeline';
import { IStudioViewModelContext } from './i-studio-view-model-context';
import { StudioPlaybackController } from './studio-playback-controller';
import { StudioSegmentController } from './studio-segment-controller';
import { EffectAreaInteractionHandler } from './effect-area-interaction-handler';
import { StudioAudioManager } from './studio-audio-manager';

/**
 * Frame-by-frame rendering engine for the Content Control Studio.
 *
 * Drives a `requestAnimationFrame` loop that synchronises visual effects, audio
 * manipulations, and subtitle overlays with video playback. Each frame the loop:
 *
 * 1. **Frame throttling** — skips redundant callbacks that fall within the same
 *    video frame duration (based on `timeline.oneFrameSecs`), unless the video is
 *    paused so the UI can still update while playback is stopped.
 *
 * 2. **Active-event resolution** — determines which {@link ContentControlledEditingDirective}
 *    directives overlap the current playback position.
 *
 * 3. **Per-frame processing** (three mutually exclusive branches):
 *    - *Cut event detected* — seeks the video past the cut region and skips the
 *      remainder of the frame.
 *    - *Active events present* — syncs the video element on the canvas, toggles
 *      beep/mute audio state, renders canvas effects (blur, mosaic, black shapes),
 *      applies audio muting, and composites edited subtitles.
 *    - *No active events* — cleans up expired directives, clears all canvas
 *      effects, stops beeps, and restores audio.
 *
 * 4. **Canvas effect rendering** — delegates to {@link ContentControlCanvasService}
 *    to draw blur, mosaic, or black rectangles / ovals / full-screen overlays.
 *
 * 5. **Video element tracking** — detects when the active
 *    {@link IContentControlVideoSource} changes and reinitialises the canvas.
 *
 * 6. **Subtitle compositing** — builds per-language subtitle text from the current
 *    playback position, then applies directive overrides (edit or remove subtitles).
 *
 * 7. **Directive cleanup** — removes expired directives from the manager and
 *    segment list, resets canvas state, and triggers a deferred timeline rebuild.
 */
export class StudioRenderLoop {
  private _lastFrameTime = 0;
  private _lastVideoElement: IContentControlVideoSource | null = null;
  private _lastHandledCutId: number | null = null;
  private _lastCutLogTime = 0;
  private _rafId: number | null = null;
  private _stopped = false;
  // Cached bound reference — avoids allocating a new function object on every frame.
  private _boundAnimationLoop = (time: number): void => this.playControllerAnimationLoop(time);

  constructor(
    private readonly _manager: IContentControlManager,
    private readonly _canvasService: ContentControlCanvasService,
    private readonly _timeline: IContentControlStudioTimeline,
    private readonly _context: IStudioViewModelContext,
    private readonly _playback: StudioPlaybackController,
    private readonly _segments: StudioSegmentController,
    private readonly _effectArea: EffectAreaInteractionHandler,
    private readonly _audio: StudioAudioManager,
  ) {}

  /**
   * Starts the render loop. Cancels any previously scheduled frame first so
   * repeated calls never stack multiple concurrent loops.
   */
  public start(): void {
    this._stopped = false;
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
    }
    this._rafId = requestAnimationFrame(this._boundAnimationLoop);
  }

  /**
   * Stops the render loop permanently (until {@link start} is called again).
   * Must be called when the owning view model is destroyed — a leaked loop keeps
   * feeding the shared canvas service positions computed from a stale video
   * source, freezing effect rectangles for the next studio instance.
   */
  public stop(): void {
    this._stopped = true;
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  private _scheduleNextFrame(): void {
    if (this._stopped) return;
    this._rafId = requestAnimationFrame(this._boundAnimationLoop);
  }

  /**
   * Returns all editing directives whose time range covers the current video playback position.
   * Returns an empty array when no timeline is available.
   */
  public getActiveEvents(): ContentControlledEditingDirective[] {
    const timeline = this._timeline;
    if (!timeline) return [];
    const videoSource = this._context.getVideoSource();
    let currentTime = videoSource.currentPlayTimeAsClosestFrameMs();
    if (this._context.shouldUseTimestampForMarkIn()) {
      currentTime = this._context.convertPlayerTimeToTimestampMs(currentTime / 1000);
    }
    const result = this._manager.editingDirectives.filter(
      (directive) => directive.startsAtMs <= currentTime && directive.endsAtMs >= currentTime,
    );
    return result;
  }

  /**
   * The `requestAnimationFrame` callback that drives the studio render loop.
   * Skips frames that fall within the same video frame as the last rendered frame (unless paused),
   * processes canvas effects, subtitle overlays, cut-event seeking, beep/mute state, and schedules
   * the next frame via `requestAnimationFrame`.
   *
   * @param currentTime - The high-resolution timestamp provided by `requestAnimationFrame`.
   */
  public playControllerAnimationLoop(currentTime: number): void {
    if (this._stopped) return;
    if (!this._shouldRunFrame(currentTime)) {
      this._scheduleNextFrame();
      return;
    }

    try {
      const videoSource = this._context.getVideoSource();
      let currentTimeMs = videoSource.currentPlayTimeAsClosestFrameMs();
      if (this._context.shouldUseTimestampForMarkIn()) {
        currentTimeMs = this._context.convertPlayerTimeToTimestampMs(currentTimeMs / 1000);
      }
      const subtitles = this._buildSubtitlesMap(currentTimeMs);
      const activeEvts = this.getActiveEvents();

      this._processFrame(currentTimeMs, subtitles, activeEvts);

      this._canvasService.updateSubtitles(Object.values(subtitles));
      this._lastFrameTime = currentTime;
    } catch (err) {
      console.error('studio-render-loop error:', err);
    }

    this._scheduleNextFrame();
  }

  private _shouldRunFrame(currentTime: number): boolean {
    const timeline = this._timeline;
    return (
      currentTime - this._lastFrameTime >= timeline.oneFrameSecs ||
      this._context.getVideoSource()?.paused === true
    );
  }

  /**
   * Handles the three rendering branches for a single frame: cut-event seek, active events, and
   * idle cleanup. Delegates canvas writes and beep/mute state changes to subordinate methods.
   *
   * @param currentTimeMs - Current video playback position in milliseconds.
   * @param subtitles - Subtitle map keyed by language, mutated in-place as events are applied.
   * @param activeEvts - Editing directives that overlap the current playback position.
   */
  private _processFrame(
    currentTimeMs: number,
    subtitles: Record<string, string[]>,
    activeEvts: ContentControlledEditingDirective[],
  ): void {
    const timeline = this._timeline;

    let shouldContinueSignal = true;
    this._handleCutEvent(activeEvts, timeline, (signal) => { shouldContinueSignal = signal; });

    if (!shouldContinueSignal) {
      // Cut detected & seek issued — skip everything this frame
    } else if (activeEvts.length > 0) {
      const video = this._context.getVideoSource(activeEvts);
      this._syncVideoElement(video);
      this._handleBeepAndMute(activeEvts);

      activeEvts.forEach((activeEvent) => {
        this._applyVideoOrExternalEvent(activeEvent);
        this._applyAudioEvent(activeEvent, activeEvts);
        this._applySubtitlesForEvent(activeEvent, currentTimeMs, subtitles);
      });
    } else {
      const mainVideo = this._context.getVideoSource();
      this._syncVideoElement(mainVideo);
      this._cleanupInactiveDirectivesAndResetCanvas();
    }
  }

  /**
   * Reinitialises the canvas video element when it has changed, and clears the cached reference
   * when the video element becomes null.
   *
   * @param video - The current video source, or null when no video is active.
   */
  private _syncVideoElement(video: IContentControlVideoSource | null): void {
    if (video != null && video !== this._lastVideoElement) {
      this._canvasService.reInitVideoElement(video);
      this._lastVideoElement = video;
    } else if (video == null) {
      this._lastVideoElement = null;
    }
  }

  private _buildSubtitlesMap(currentTimeMs: number): Record<string, string[]> {
    const subtitles: Record<string, string[]> = {};
    const options = this._audio?.selectedSubtitleOptions ?? [];
    options.forEach((option) => {
      const subtitleSegments = this._manager.getCurrentSubtitleSegments(option, currentTimeMs);
      if (subtitleSegments && Array.isArray(subtitleSegments) && subtitleSegments.length > 0) {
        const canvasSubs = subtitleSegments.map((segment) => segment.Text);
        if (canvasSubs.length > 0) subtitles[option] = canvasSubs;
      }
    });
    return subtitles;
  }

  private _handleCutEvent(
    activeEvts: ContentControlledEditingDirective[],
    timeline: IContentControlStudioTimeline,
    setContinueSignal: (value: boolean) => void,
  ): void {
    
    const currentPlayTimeSecs = timeline.currentVideoPlayTimeSecs();
    const videoSource = this._context.getVideoSource(activeEvts);

    const cutEvent = timeline.getCutSegmentAtSpecificTime(currentPlayTimeSecs);
    const now = performance.now();
    const shouldLog = now - this._lastCutLogTime >= 1000;

    if (!cutEvent || videoSource?.paused !== false) {
      if (shouldLog) {
        this._lastCutLogTime = now;
        /*console.log(
          '%c[CUT] No cut event at currentPlayTimeSecs=%f',
          'color: orange; font-weight: bold',
          currentPlayTimeSecs,
        );*/
      }
      this._lastHandledCutId = null;
      return;
    }

    if (shouldLog) {
      this._lastCutLogTime = now;
      console.log(
        '%c[CUT] Cut event found — id=%d, startsAtMs=%d, endsAtMs=%d, currentPlayTimeSecs=%f',
        'color: limegreen; font-weight: bold',
        cutEvent.id,
        cutEvent.startsAtMs,
        cutEvent.endsAtMs,
        currentPlayTimeSecs,
      );
    }

    if (this._lastHandledCutId === cutEvent.id) {
      return;
    }

    this._lastHandledCutId = cutEvent.id;
    setContinueSignal(false);
    let afterCutEventTime = timeline.getSeekTimeAfterCutEnd(cutEvent.endsAtMs);
    const cutDurationSecs = (cutEvent.endsAtMs - cutEvent.startsAtMs) / 1000;
    const actualPlayerTimeSecs = videoSource.rawCurrentTimeSecs;
    if (afterCutEventTime <= actualPlayerTimeSecs) {
      afterCutEventTime = actualPlayerTimeSecs + cutDurationSecs;
    }
    console.log(
      '%c[CUT] Seeking via seekToVideoPlayerRelativeTimeInSecs — afterCutEventTime=%f, cutDurationSecs=%f, actualPlayerTimeSecs=%f, cutEvent.id=%d',
      'color: red; font-weight: bold',
      afterCutEventTime,
      cutDurationSecs,
      actualPlayerTimeSecs,
      cutEvent.id,
    );
    timeline.seekToVideoPlayerRelativeTimeInSecs(afterCutEventTime);
  }

  private _handleBeepAndMute(activeEvts: ContentControlledEditingDirective[]): void {
    if (activeEvts.every((event) => event.action !== ContentControlledEventActionEnum.Beep)) {
      this._manager.stopBeep();
    }
    if (
      activeEvts.every((event) => event.action !== ContentControlledEventActionEnum.Mute) &&
      this._playback.isAudioForcedMuted === false &&
      this._playback.isAudioMuted
    ) {
      this._playback.toggleMute();
    }
  }

  private _applyVideoOrExternalEvent(
    event: ContentControlledEditingDirective,
  ): void {
    if (
      event.mediaType !== ContentControlledMediaTypeEnum.Video &&
      event.mediaType !== ContentControlledMediaTypeEnum.External
    ) {
      return;
    }
    const selectedEvent =
      this._manager.selectedSegment && event.id === this._manager.selectedSegment.id;
    const canvasObject = this._effectArea.getCanvasObjectFromEvent(
      event,
      false,
      selectedEvent ? this._effectArea.keyframeChangeHolder : undefined,
    );
    if (canvasObject === null || canvasObject === undefined) return;
    this._applyDragOverlayIfNeeded(event, canvasObject);
    this._renderCanvasEffect(event, canvasObject);
  }

  private _applyDragOverlayIfNeeded(
    event: ContentControlledEditingDirective,
    canvasObject: ICanvasBaseObject,
  ): void {
    if (
      (this._effectArea.isEffectAreaDragging || this._effectArea.isEffectAreaResizing) &&
      this._manager.selectedSegment &&
      event.id === this._manager.selectedSegment.id &&
      this._effectArea.keyframeChangeHolder
    ) {
      canvasObject.ingest(this._effectArea.keyframeChangeHolder.exportPlacement());
    }
  }

  private _renderCanvasEffect(
    event: ContentControlledEditingDirective,
    canvasObject: ICanvasBaseObject,
  ): void {
    switch (event.action) {
      case ContentControlledEventActionEnum.BlackScreen:
      case ContentControlledEventActionEnum.BlackRect:
        this._canvasService.addBlackRectangle(canvasObject as CCRect);
        break;
      case ContentControlledEventActionEnum.PixelateScreen:
      case ContentControlledEventActionEnum.PixelateRect:
        this._canvasService.addMosaicRectangle(canvasObject as CCMosaicRect);
        break;
      case ContentControlledEventActionEnum.BlurScreen:
      case ContentControlledEventActionEnum.BlurRect:
        this._canvasService.addBlurRectangle(canvasObject as CCBlurRect);
        break;
      case ContentControlledEventActionEnum.PixelateOval:
        this._canvasService.addMosaicCircle(canvasObject as CCMosaicRect);
        break;
      case ContentControlledEventActionEnum.BlurOval:
        this._canvasService.addBlurCircle(canvasObject as CCBlurRect);
        break;
      case ContentControlledEventActionEnum.BlackOval:
        this._canvasService.addBlackCircle(canvasObject as CCRect);
        break;
    }
  }

  private _applyAudioEvent(
    event: ContentControlledEditingDirective,
    activeEvts: ContentControlledEditingDirective[],
  ): void {
    if (event.mediaType !== ContentControlledMediaTypeEnum.Audio) return;
    if (this._context.getVideoSource(activeEvts)?.paused === false) {
      if (
        this._playback.isAudioForcedMuted === false &&
        this._playback.isAudioMuted === false
      ) {
        this._playback.toggleMute();
        if (event.action === ContentControlledEventActionEnum.Beep && event.instructions?.beepFrequency !== undefined) {
          this._manager.startBeep(event.instructions.beepFrequency);
        }
      }
    } else {
      this._manager.stopBeep();
      this._playback.toggleMute(true);
    }
  }

  private _applySubtitlesForEvent(
    event: ContentControlledEditingDirective,
    currentTimeMs: number,
    subtitles: Record<string, string[]>,
  ): void {
    if (event.mediaType !== ContentControlledMediaTypeEnum.Subtitles) return;
    switch (event.action) {
      case ContentControlledEventActionEnum.EditSubtitles: {
        if (!event.subtitles) break;
        Object.keys(subtitles).forEach((lang) => {
          if (event.subtitles!.segmentsMap[lang]) {
            const adjustedTime = this._manager.adjustTimeToSubtitle(currentTimeMs, lang);
            const matchingSegments = event.subtitles.segmentsMap[lang].filter(
              (segment) =>
                adjustedTime >= segment.StartTimeMS && adjustedTime <= segment.EndTimeMS,
            );
            if (matchingSegments && Array.isArray(matchingSegments) && matchingSegments.length > 0) {
              subtitles[lang] = matchingSegments.map((segment) => segment.Text);
            }
          }
        });
        break;
      }
      case ContentControlledEventActionEnum.RemoveSubtitles: {
        for (const k of Object.keys(subtitles)) {
          subtitles[k] = [];
        }
        break;
      }
    }
  }

  private _cleanupInactiveDirectivesAndResetCanvas(): void {
    const timeline = this._timeline;
    const editingDirectivesToDelete = this._context.cleanupOldDirectives(
      this._manager.editingDirectives,
      this._manager.selectedSegment?.id,
    );
    if (editingDirectivesToDelete.length > 0) {
      const segments = this._segments.segments;
      editingDirectivesToDelete.forEach((directive) => {
        this._manager.removeEditingDirectiveEvent(directive, false);
        if (
          this._manager.selectedSegment &&
          this._manager.selectedSegment.id === directive.id
        ) {
          this._manager.selectedSegment = null;
          this._canvasService.drawEffectAreaSVG = false;
        }
        const index = segments.findIndex((segment) => segment.id === directive.id);
        if (index > -1) segments.splice(index, 1);
      });
      // Defer rebuild to avoid re-entrancy and let the current stack complete.
      setTimeout(() => timeline.rebuildSegmentsInternally(segments), 1);
    }
    this._canvasService.clearAllRenderingObjects();
    this._manager.stopBeep();
    if (this._playback.isAudioForcedMuted === false && this._playback.isAudioMuted) {
      this._playback.toggleMute();
    }
  }
}
